import { assetUrl } from "@ondit/publishing";
import { MediaEditor, type MediaEntry } from "@ondit/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useWorkspaceFiles, workspaceFilesKeys } from "@/hooks/queries/workspace-files";
import { uploadWorkspaceFileSession } from "@/lib/uploads/client/session-upload";
import { fileUrl } from "~/common/media/api";
import { pick, useI18n } from "~/core/i18n";
import { GenerateMediaDialog, generatable } from "./generate-media";

export function useBrandMedia(brand: string) {
	const query = useWorkspaceFiles(brand);
	const client = useQueryClient();
	const { locale } = useI18n();
	const files: MediaEntry[] = (query.data ?? [])
		.filter((f) => /^(image|video|audio)\//.test(f.type))
		.map((f) => ({ id: `ondit-file:${f.key}`, url: fileUrl(f.key), name: f.name, type: f.type, alt: "" }));
	async function upload(file: File): Promise<MediaEntry> {
		const result = await uploadWorkspaceFileSession({ workspaceId: brand, file });
		await client.invalidateQueries({ queryKey: workspaceFilesKeys.workspaceLists(brand) });
		const fresh = await query.refetch();
		const record = fresh.data?.find((f) => f.id === result.id);
		if (!record)
			throw new Error(
				locale === "zh" ? "文件已上传，请刷新素材库后选择。" : "File uploaded. Refresh Files to select it.",
			);
		return { id: `ondit-file:${record.key}`, url: fileUrl(record.key), name: record.name, type: record.type, alt: "" };
	}
	function resolve(media: { url: string; alt: string }[]): MediaEntry[] {
		return media.map((m) => {
			const file = files.find((f) => f.id === m.url);
			return file
				? { ...file, alt: m.alt }
				: {
						id: m.url,
						url: m.url.startsWith("ondit-file:") ? fileUrl(m.url.slice(11)) : assetUrl(m.url) ? m.url : "",
						name: m.url.startsWith("ondit-file:")
							? locale === "zh"
								? "文件素材"
								: "File asset"
							: m.url.split("/").pop() || m.url,
						type: /\.(mp4|mov|webm)(?:[?#]|$)/i.test(m.url) ? "video/mp4" : "image/*",
						alt: m.alt,
					};
		});
	}
	return {
		brand,
		files,
		upload,
		resolve,
		loading: query.isPending,
		error: query.error
			? locale === "zh"
				? "素材库加载失败，请重新打开重试。"
				: "Could not load Files. Reopen to retry."
			: undefined,
		refresh: () => {
			void query.refetch();
		},
	};
}
export function BrandMediaEditor({
	media,
	value,
	onChange,
	disabled,
	accept,
	maxCount,
	onBusyChange,
	allowUrl,
}: {
	media: ReturnType<typeof useBrandMedia>;
	value: { url: string; alt: string }[];
	onChange: (value: { url: string; alt: string }[]) => void;
	disabled?: boolean;
	accept?: string;
	maxCount?: number;
	onBusyChange?: (busy: boolean) => void;
	allowUrl?: boolean;
}) {
	const { locale } = useI18n();
	const [generating, setGenerating] = useState(false);
	// Generation is an upload source like the disk is: whatever this control
	// accepts is what it can be asked to make, so a caller gets the generators
	// its own media rule allows without wiring any of them up.
	const kinds = generatable(accept);
	const full = value.length >= (maxCount ?? 20);
	return (
		<>
			<MediaEditor
				key={media.brand}
				onGenerate={kinds.length && !disabled && !full ? () => setGenerating(true) : undefined}
				generateLabel={pick(locale, ["Generate media", "生成素材"])}
				allowUrl={allowUrl}
				onBusyChange={onBusyChange}
				value={media.resolve(value)}
				onChange={(entries) => onChange(entries.map((e) => ({ url: e.id, alt: e.alt })))}
				disabled={disabled}
				accept={accept}
				maxCount={maxCount}
				locale={locale}
				upload={media.upload}
				library={media.files}
				libraryLoading={media.loading}
				libraryError={media.error}
				refreshLibrary={media.refresh}
			/>
			{!!kinds.length && (
				<GenerateMediaDialog
					brandId={media.brand}
					modalities={kinds}
					open={generating}
					onOpenChange={setGenerating}
					disabled={disabled || full}
					onUse={(fileId, alt) => {
						onChange([...value, { url: `ondit-file:${fileId}`, alt }]);
						media.refresh();
					}}
				/>
			)}
		</>
	);
}
